import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Zap, Check, Settings, ExternalLink, Info } from "lucide-react";

export default function AutoApply() {
  const [rules, setRules] = useState({ enabled: false, min_match_score: 90, require_remote: false });
  const [queue, setQueue] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [approved, setApproved] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get("/auto-apply/queue");
    setRules((prev) => r.data.rules || prev);
    setQueue(r.data.queue);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRules = async () => {
    await api.post("/auto-apply/rules", rules);
    load();
  };

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const approve = async () => {
    if (selected.size === 0) return;
    const first = queue.find((q) => selected.has(q.job_id) && q.url);
    if (first) window.open(first.url, "_blank", "noopener");
    const r = await api.post("/auto-apply/approve", { job_ids: Array.from(selected) });
    setApproved(r.data.applications || []);
    setSelected(new Set());
    load();
  };

  return (
    <div className="space-y-6" data-testid="auto-apply-page">
      <div className="jr-card border border-cyan-500/30 flex items-start gap-3 text-sm text-zinc-300" data-testid="how-apply-works-banner">
        <Info className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
        <div>
          <span className="text-white font-medium">How applying works: </span>
          approving adds jobs to your Applications tracker and gives you a direct link to each original posting —
          you complete the final submission on the job board (LinkedIn, Indeed, Glassdoor or Naukri) so it's a real,
          verified application under your own account.
        </div>
      </div>
      <div className="jr-card" data-testid="auto-apply-rules-card">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-violet-300" />
          <div className="font-semibold text-white">Auto-Apply Rules</div>
          <span className="jr-pill bg-amber-500/20 text-amber-200 text-[10px]">BETA</span>
        </div>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <label className="flex items-center gap-3" data-testid="enable-toggle">
            <input type="checkbox" checked={rules.enabled} onChange={(e) => setRules({ ...rules, enabled: e.target.checked })} className="w-4 h-4" />
            <span className="text-zinc-200">Enable auto-queue</span>
          </label>
          <div>
            <div className="text-xs text-zinc-400 mb-1">Min Match Score: {rules.min_match_score}%</div>
            <input data-testid="min-score-slider" type="range" min="50" max="99" value={rules.min_match_score}
              onChange={(e) => setRules({ ...rules, min_match_score: parseInt(e.target.value) })} className="w-full" />
          </div>
          <label className="flex items-center gap-3" data-testid="remote-toggle">
            <input type="checkbox" checked={rules.require_remote} onChange={(e) => setRules({ ...rules, require_remote: e.target.checked })} className="w-4 h-4" />
            <span className="text-zinc-200">Remote only</span>
          </label>
        </div>
        <button onClick={saveRules} className="jr-btn-primary mt-4 text-sm" data-testid="save-rules-btn">Save Rules</button>
      </div>

      <div className="jr-card">
        <div className="flex justify-between mb-4">
          <div className="font-semibold text-white flex items-center gap-2"><Zap className="w-5 h-5 text-amber-300" /> Approval Queue ({queue.length})</div>
          <button onClick={approve} disabled={selected.size === 0} className="jr-btn-primary text-sm" data-testid="approve-selected-btn">
            <Check className="w-4 h-4 inline mr-1" /> Approve &amp; Track ({selected.size})
          </button>
        </div>
        {approved.length > 0 && (
          <div className="mb-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30" data-testid="approved-panel">
            <div className="text-sm text-emerald-300 font-medium mb-2">
              ✓ {approved.length} job{approved.length > 1 ? "s" : ""} added to your Applications tracker. Now finish each one on the job board:
            </div>
            <div className="space-y-1.5">
              {approved.map((a) => (
                <a key={a.application_id} href={a.url || "#"} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-cyan-300 hover:text-cyan-200 underline" data-testid="complete-application-link">
                  <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                  Complete application: {a.job_title} at {a.company}
                </a>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-2" data-testid="approval-queue">
          {queue.length === 0 && <div className="text-zinc-500 text-sm text-center py-8">No jobs match your rules yet. Lower the threshold or search more jobs.</div>}
          {queue.map((j) => (
            <label key={j.job_id} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer" data-testid="queue-item">
              <input type="checkbox" checked={selected.has(j.job_id)} onChange={() => toggle(j.job_id)} className="w-4 h-4" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">{j.title}</div>
                <div className="text-xs text-zinc-400">{j.company} • {j.location}</div>
              </div>
              <span className="jr-pill bg-emerald-500/15 text-emerald-300">{j.match_score}%</span>
              {j.url && (
                <a href={j.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                  className="text-zinc-400 hover:text-cyan-300 shrink-0" title="View original posting" data-testid="queue-view-job-link">
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
